import { config } from "@financial-advisory/config";
import { store } from "./store";
import { toStreamError } from "./streamErrors";

export interface SimulationRunRequestPayload {
  persona_detail: string;
  situation_summary: string;
  session_id?: string | null;
  max_messages?: number;
  should_terminate_early?: boolean;
  execute_output_guardrails?: boolean;
  execute_input_guardrails?: boolean;
  conversation_history?: { role: string; content: string }[];
}

export interface SimulationInitialMessagesEvent {
  type: "initial_messages";
  messages: string[];
}

export interface SimulationUserMessageEvent {
  type: "user_message";
  content: string;
  turn: number;
}

export interface SimulationAssistantMessageEvent {
  type: "assistant_message";
  content: string;
  session_id: string;
  session_version_id: string;
  is_complete: boolean;
  target_questions: string[];
  turn: number;
}

export interface SimulationUserTypingEvent {
  type: "user_typing";
  turn: number;
}

export interface SimulationCompleteEvent {
  type: "simulation_complete";
  session_id: string;
  total_turns: number;
}

export interface SimulationErrorEvent {
  type: "simulation_error";
  error: string;
}

export type SimulationSSEEvent =
  | SimulationInitialMessagesEvent
  | SimulationUserMessageEvent
  | SimulationUserTypingEvent
  | SimulationAssistantMessageEvent
  | SimulationCompleteEvent
  | SimulationErrorEvent;

export interface SimulationSSECallbacks {
  onInitialMessages?: (event: SimulationInitialMessagesEvent) => void;
  onUserMessage?: (event: SimulationUserMessageEvent) => void;
  onUserTyping?: (event: SimulationUserTypingEvent) => void;
  onAssistantMessage?: (event: SimulationAssistantMessageEvent) => void;
  onSimulationComplete?: (event: SimulationCompleteEvent) => void;
  onError?: (error: Error) => void;
  onStreamEnd?: () => void;
}

export function streamSimulation(
  payload: SimulationRunRequestPayload,
  callbacks: SimulationSSECallbacks,
  abortSignal: AbortSignal,
): void {
  const baseUrl = config.API_BASE_URL;
  const { auth } = store.getState();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth.accessToken) {
    headers["Authorization"] = `Bearer ${auth.accessToken}`;
  }

  fetch(`${baseUrl}/simulate/run`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    signal: abortSignal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Simulation run failed: ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          continue;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          const jsonStr = trimmed.slice(6);
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr) as SimulationSSEEvent;
            switch (event.type) {
              case "initial_messages":
                callbacks.onInitialMessages?.(event);
                break;
              case "user_message":
                callbacks.onUserMessage?.(event);
                break;
              case "user_typing":
                callbacks.onUserTyping?.(event);
                break;
              case "assistant_message":
                callbacks.onAssistantMessage?.(event);
                break;
              case "simulation_complete":
                callbacks.onSimulationComplete?.(event);
                break;
              case "simulation_error":
                callbacks.onError?.(new Error(event.error));
                break;
            }
          } catch {
            // skip malformed events
          }
        }
      }

      callbacks.onStreamEnd?.();
    })
    .catch((err) => {
      if (abortSignal.aborted) return;
      callbacks.onError?.(toStreamError(err));
    });
}
